// pages/Admin/PlatformFeeManagement.jsx
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  getPlatformFees,
  createPlatformFee,
  updatePlatformFee,
  deletePlatformFee,
  getCurrentPlatformFee,
  getPlatformFeeStats,
  calculatePlatformFee
} from '../../Services/Operations/ProductAPI';
import '../../Styles/ManagementPages.css';

const PlatformFeeManagement = () => {
  const { token } = useSelector(state => state.user);
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentFee, setCurrentFee] = useState(null);
  const [stats, setStats] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [calculationResult, setCalculationResult] = useState(null);
  const [calculatorAmount, setCalculatorAmount] = useState('');
  const [calculatorType, setCalculatorType] = useState('all');
  const [formData, setFormData] = useState({
    feeType: 'percentage',
    amount: '',
    percentage: '',
    minAmount: '',
    maxAmount: '',
    applicableFor: ['all'],
    description: '',
    effectiveFrom: new Date().toISOString().slice(0, 16),
    effectiveTo: '',
    isActive: true,
    deactivatePrevious: false
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({ isActive: '', feeType: '' });
  const pageSize = 10;

  useEffect(() => {
    fetchCurrentFee();
    fetchStats();
    fetchFees();
  }, [currentPage, filters]);

  const fetchCurrentFee = async () => {
    try {
      const response = await getCurrentPlatformFee(token);
      if (response.success) {
        setCurrentFee(response.data);
      }
    } catch (err) {
      console.error('Error fetching current fee:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getPlatformFeeStats(token);
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

const fetchFees = async () => {
  try {
    setLoading(true);
    const response = await getPlatformFees(token, currentPage, pageSize, filters);
    console.log('Fetched fees response:', response); // Add this for debugging
    
    if (response.success) {
      // Check if response.data is an array
      const feesData = Array.isArray(response.data) ? response.data : [];
      setFees(feesData);
      
      // Handle pagination info
      if (response.pagination) {
        setTotalPages(response.pagination.pages || 1);
        setTotalItems(response.pagination.total || 0);
      } else {
        // If no pagination object, set based on array length
        setTotalPages(1);
        setTotalItems(feesData.length);
      }
    } else {
      setFees([]);
      setTotalPages(1);
      setTotalItems(0);
    }
  } catch (err) {
    setError('Failed to fetch platform fees');
    console.error(err);
    setFees([]);
  } finally {
    setLoading(false);
  }
};

  const handleCalculateFee = async () => {
    if (!calculatorAmount || calculatorAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    
    try {
      const response = await calculatePlatformFee(
        parseFloat(calculatorAmount), 
        calculatorType, 
        token
      );
      if (response.success) {
        setCalculationResult(response.data);
        setError(null);
      }
    } catch (err) {
      setError('Failed to calculate fee');
      console.error(err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'applicableFor') {
        const currentValues = formData.applicableFor;
        if (checked) {
          setFormData({ ...formData, applicableFor: [...currentValues, value] });
        } else {
          setFormData({ ...formData, applicableFor: currentValues.filter(v => v !== value) });
        }
      } else {
        setFormData({ ...formData, [name]: checked });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    try {
      const submitData = {
        ...formData,
        amount: formData.feeType === 'fixed' ? parseFloat(formData.amount) : 0,
        percentage: formData.feeType === 'percentage' ? parseFloat(formData.percentage) : 0,
        minAmount: formData.minAmount ? parseFloat(formData.minAmount) : 0,
        maxAmount: formData.maxAmount ? parseFloat(formData.maxAmount) : null,
        effectiveFrom: new Date(formData.effectiveFrom),
        effectiveTo: formData.effectiveTo ? new Date(formData.effectiveTo) : null
      };
      
      let response;
      if (editingFee) {
        response = await updatePlatformFee(editingFee._id, submitData, token);
      } else {
        response = await createPlatformFee(submitData, token);
      }
      
      if (response.success) {
        setSuccess(response.message);
        setShowModal(false);
        resetForm();
        fetchFees();
        fetchCurrentFee();
        fetchStats();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save platform fee');
    }
  };

  const handleEdit = (fee) => {
    setEditingFee(fee);
    setFormData({
      feeType: fee.feeType,
      amount: fee.amount || '',
      percentage: fee.percentage || '',
      minAmount: fee.minAmount || '',
      maxAmount: fee.maxAmount || '',
      applicableFor: fee.applicableFor,
      description: fee.description || '',
      effectiveFrom: fee.effectiveFrom ? new Date(fee.effectiveFrom).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16),
      effectiveTo: fee.effectiveTo ? new Date(fee.effectiveTo).toISOString().slice(0, 16) : '',
      isActive: fee.isActive,
      deactivatePrevious: false
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this platform fee?')) {
      try {
        const response = await deletePlatformFee(id, token);
        if (response.success) {
          setSuccess('Platform fee deleted successfully');
          fetchFees();
          fetchCurrentFee();
          fetchStats();
          setTimeout(() => setSuccess(null), 3000);
        }
      } catch (err) {
        setError('Failed to delete platform fee');
      }
    }
  };

  const resetForm = () => {
    setEditingFee(null);
    setFormData({
      feeType: 'percentage',
      amount: '',
      percentage: '',
      minAmount: '',
      maxAmount: '',
      applicableFor: ['all'],
      description: '',
      effectiveFrom: new Date().toISOString().slice(0, 16),
      effectiveTo: '',
      isActive: true,
      deactivatePrevious: false
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
    setCurrentPage(1);
  };

  const getApplicableForText = (applicableFor) => {
    if (applicableFor.includes('all')) return 'All Bookings';
    return applicableFor.map(a => a.replace('_', ' ')).join(', ');
  };

  if (loading && fees.length === 0) {
    return (
      <div className="management-page">
        <div className="loading-container">
          <p>Loading platform fees...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Platform Fee Management</h1>
        <div className="header-actions">
          <button className="add-btn" onClick={() => {
            resetForm();
            setShowModal(true);
          }}>
            + Add Platform Fee
          </button>
          <button className="export-btn" onClick={() => setShowCalculator(!showCalculator)}>
            {showCalculator ? 'Hide Calculator' : 'Show Calculator'}
          </button>
        </div>
      </div>

      {/* Current Active Fee Banner */}
      {currentFee && currentFee.feeType && (
        <div className="current-fee-banner">
          <div className="banner-content">
            <h3>📊 Currently Active Fee Structure</h3>
            <div className="fee-details">
              {currentFee.feeType === 'percentage' ? (
                <span className="fee-badge percentage">
                  {currentFee.percentage}% Platform Fee
                </span>
              ) : (
                <span className="fee-badge fixed">
                  ₹{currentFee.amount} Fixed Platform Fee
                </span>
              )}
              {currentFee.minAmount > 0 && (
                <span className="fee-note">Min Fee: ₹{currentFee.minAmount}</span>
              )}
              {currentFee.maxAmount && (
                <span className="fee-note">Max Fee: ₹{currentFee.maxAmount}</span>
              )}
              <span className="fee-note">
                Effective from: {new Date(currentFee.effectiveFrom).toLocaleDateString()}
              </span>
              {currentFee.effectiveTo && (
                <span className="fee-note">
                  to {new Date(currentFee.effectiveTo).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fee Calculator */}
      {showCalculator && (
        <div className="fee-calculator">
          <h3>💰 Fee Calculator</h3>
          <div className="calculator-form">
            <div className="form-row">
              <div className="form-group">
                <label>Booking Amount (₹)</label>
                <input
                  type="number"
                  value={calculatorAmount}
                  onChange={(e) => setCalculatorAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Booking Type</label>
                <select
                  value={calculatorType}
                  onChange={(e) => setCalculatorType(e.target.value)}
                  className="form-control"
                >
                  <option value="bus_booking">Bus Booking</option>
                </select>
              </div>
              <div className="form-group">
                <label>&nbsp;</label>
                <button onClick={handleCalculateFee} className="calculate-btn">
                  Calculate
                </button>
              </div>
            </div>
            
            {calculationResult && (
              <div className="calculation-result">
                <h4>Calculation Result</h4>
                <div className="result-details">
                  <div className="result-row">
                    <span>Booking Amount:</span>
                    <strong>₹{calculationResult.finalAmount - calculationResult.feeAmount}</strong>
                  </div>
                  <div className="result-row">
                    <span>Platform Fee ({calculationResult.feeType === 'percentage' ? `${calculationResult.percentage}%` : 'Fixed'}):</span>
                    <strong className="fee-amount">+ ₹{calculationResult.feeAmount}</strong>
                  </div>
                  {calculationResult.minAmount > 0 && (
                    <div className="result-row min-max">
                      <span>Minimum Fee Applied:</span>
                      <span>₹{calculationResult.minAmount}</span>
                    </div>
                  )}
                  {calculationResult.maxAmount && (
                    <div className="result-row min-max">
                      <span>Maximum Fee Applied:</span>
                      <span>₹{calculationResult.maxAmount}</span>
                    </div>
                  )}
                  <div className="result-row total">
                    <span>Total Amount:</span>
                    <strong>₹{calculationResult.finalAmount}</strong>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="stats-cards">
          <div className="stat-card">
            <h3>Total Configurations</h3>
            <p>{stats.totalFees}</p>
            <span>All fee structures</span>
          </div>
          <div className="stat-card">
            <h3>Active Configurations</h3>
            <p>{stats.activeFees}</p>
            <span>Currently active</span>
          </div>
          <div className="stat-card">
            <h3>Percentage Based</h3>
            <p>{stats.percentageFees}</p>
            <span>Percentage fee structures</span>
          </div>
          <div className="stat-card">
            <h3>Fixed Amount</h3>
            <p>{stats.fixedFees}</p>
            <span>Fixed fee structures</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <select name="isActive" value={filters.isActive} onChange={handleFilterChange} className="filter-select">
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <select name="feeType" value={filters.feeType} onChange={handleFilterChange} className="filter-select">
          <option value="">All Types</option>
          <option value="percentage">Percentage</option>
          <option value="fixed">Fixed</option>
        </select>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Fee Type</th>
              <th>Value</th>
              <th>Min/Max Fee</th>
              <th>Applicable For</th>
              <th>Status</th>
              <th>Effective Period</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {fees.map((fee, index) => (
              <tr key={fee._id}>
                <td>{(currentPage - 1) * pageSize + index + 1}</td>
                <td>
                  <span className={`fee-type-badge ${fee.feeType}`}>
                    {fee.feeType === 'percentage' ? 'Percentage' : 'Fixed'}
                  </span>
                </td>
                <td>
                  {fee.feeType === 'percentage' ? `${fee.percentage}%` : `₹${fee.amount}`}
                </td>
                <td>
                  {fee.minAmount > 0 && <div>Min: ₹{fee.minAmount}</div>}
                  {fee.maxAmount && <div>Max: ₹{fee.maxAmount}</div>}
                  {!fee.minAmount && !fee.maxAmount && <span>-</span>}
                </td>
                <td>{getApplicableForText(fee.applicableFor)}</td>
                <td>
                  <span className={`status-badge ${fee.isActive ? 'active' : 'inactive'}`}>
                    {fee.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div>From: {new Date(fee.effectiveFrom).toLocaleDateString()}</div>
                  {fee.effectiveTo && <div>To: {new Date(fee.effectiveTo).toLocaleDateString()}</div>}
                </td>
                <td>{new Date(fee.createdAt).toLocaleDateString()}</td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn edit" onClick={() => handleEdit(fee)}>
                      Edit
                    </button>
                    <button className="action-btn delete" onClick={() => handleDelete(fee._id)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {fees.length === 0 && !loading && (
          <div className="no-data">
            <p>No platform fees configured yet.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="pagination-container">
        <div className="pagination-info">
          Showing {fees.length} of {totalItems} configurations
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="page-info">Page {currentPage} of {totalPages}</span>
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content1 platform-fee-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingFee ? 'Edit Platform Fee' : 'Add Platform Fee'}</h3>
              <button className="modal-close-btn" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Fee Type Selection */}
                <div className="form-group">
                  <label>Fee Type *</label>
                  <select
                    name="feeType"
                    value={formData.feeType}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>

                {/* Percentage Field */}
                {formData.feeType === 'percentage' && (
                  <div className="form-group">
                    <label>Percentage (%) *</label>
                    <input
                      type="number"
                      name="percentage"
                      value={formData.percentage}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Enter percentage (e.g., 5 for 5%)"
                      min="0"
                      max="100"
                      step="0.01"
                      required
                    />
                  </div>
                )}

                {/* Fixed Amount Field */}
                {formData.feeType === 'fixed' && (
                  <div className="form-group">
                    <label>Fixed Amount (₹) *</label>
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Enter fixed amount"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                )}

                {/* Min/Max Amount */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Minimum Fee (₹)</label>
                    <input
                      type="number"
                      name="minAmount"
                      value={formData.minAmount}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Optional"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="form-group">
                    <label>Maximum Fee (₹)</label>
                    <input
                      type="number"
                      name="maxAmount"
                      value={formData.maxAmount}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Optional"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* Applicable For */}
                <div className="form-group">
                  <label>Applicable For *</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="applicableFor"
                        value="all"
                        checked={formData.applicableFor.includes('all')}
                        onChange={handleInputChange}
                      />
                      <span>All Bookings</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="applicableFor"
                        value="bus_booking"
                        checked={formData.applicableFor.includes('bus_booking')}
                        onChange={handleInputChange}
                      />
                      <span>Bus Bookings</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="applicableFor"
                        value="tour_booking"
                        checked={formData.applicableFor.includes('tour_booking')}
                        onChange={handleInputChange}
                      />
                      <span>Tour Bookings</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="applicableFor"
                        value="hotel_booking"
                        checked={formData.applicableFor.includes('hotel_booking')}
                        onChange={handleInputChange}
                      />
                      <span>Hotel Bookings</span>
                    </label>
                  </div>
                </div>

                {/* Effective Period */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Effective From *</label>
                    <input
                      type="datetime-local"
                      name="effectiveFrom"
                      value={formData.effectiveFrom}
                      onChange={handleInputChange}
                      className="form-control"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Effective To</label>
                    <input
                      type="datetime-local"
                      name="effectiveTo"
                      value={formData.effectiveTo}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="form-control"
                    rows="3"
                    placeholder="Optional description"
                  />
                </div>

                {/* Status and Options */}
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                    />
                    <span>Active</span>
                  </label>
                  
                  {!editingFee && (
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="deactivatePrevious"
                        checked={formData.deactivatePrevious}
                        onChange={handleInputChange}
                      />
                      <span>Deactivate all previous active fees</span>
                    </label>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="action-btn cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="action-btn save">
                  {editingFee ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformFeeManagement;